/**
 * Session Type Utility Functions
 * 
 * This module provides utilities for working with session types from the database.
 * It centralizes session type logic and capabilities while maintaining the database
 * as the single source of truth.
 */

import { supabase } from './supabase';
import { isPegBoardEnabled } from '../config/featureFlags';

export const PEG_BOARD_SESSION_TYPE_NAME = 'Peg Board';
export const TOURNAMENT_SESSION_TYPE_NAME = 'Tournament';

// Cache for session types to avoid repeated database queries
let sessionTypesCache = null;
let lastFetchTime = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch all session types from the database
 * @returns {Promise<Array>} Array of session type objects
 */
export const fetchSessionTypes = async () => {
  const now = Date.now();
  
  // Use cache if available and not expired
  if (sessionTypesCache && (now - lastFetchTime) < CACHE_TTL) {
    return sessionTypesCache;
  }
  
  // Fetch from database
  const { data, error } = await supabase
    .from('session_types')
    .select('*');
    
  if (error) {
    console.error('Error fetching session types:', error);
    return sessionTypesCache || []; // Return cache if available, empty array otherwise
  }
  
  // Update cache
  sessionTypesCache = data;
  lastFetchTime = now;
  
  return data;
};

/**
 * Get session type by name
 * @param {string} name - The session type name to find
 * @returns {Promise<Object|null>} The session type object or null if not found
 */
export const getSessionTypeByName = async (name) => {
  const types = await fetchSessionTypes();
  return types.find(type => type.name === name) || null;
};

/**
 * Get session type by ID
 * @param {string} id - The session type ID to find
 * @returns {Promise<Object|null>} The session type object or null if not found
 */
export const getSessionTypeById = async (id) => {
  const types = await fetchSessionTypes();
  return types.find(type => type.id === id) || null;
};

/**
 * Session capabilities configuration based on session type name
 * This maps session type names to their capabilities
 */
export const SESSION_CAPABILITIES = {
  // Default capabilities for any session type
  DEFAULT: {
    eloUpdates: true,
    partnershipStats: true,
    autoMatchmaking: true,
    courtClearing: false,
    playerStatsTab: true,
    socialGameLogging: false
  },
  
  // Social Session specific capabilities
  'Social Session': {
    eloUpdates: false,
    partnershipStats: false,
    autoMatchmaking: false,
    courtClearing: true,
    playerStatsTab: false,
    socialGameLogging: true
  },
  
  // Private Session specific capabilities
  'Private Session': {
    eloUpdates: false,
    partnershipStats: false,
    autoMatchmaking: false,
    courtClearing: true,  // Like social sessions
    playerStatsTab: false,
    socialGameLogging: true
  },

  // Peg Board uses competitive courts/waitlist, without Elo or auto-matchmaking.
  [PEG_BOARD_SESSION_TYPE_NAME]: {
    eloUpdates: false,
    partnershipStats: false,
    autoMatchmaking: false,
    courtClearing: false,
    playerStatsTab: true,
    socialGameLogging: false
  }
};

/**
 * Get capabilities for a session type
 * @param {string} sessionTypeName - The name of the session type
 * @returns {Object} The capabilities object for the session type
 */
export const getSessionCapabilities = (sessionTypeName) => {
  return SESSION_CAPABILITIES[sessionTypeName] || SESSION_CAPABILITIES.DEFAULT;
};

/**
 * Check if a session is a social session
 * @param {Object} session - The session object with session_types property
 * @returns {boolean} True if the session is a social session
 */
export const isSocialSession = (session) => {
  return session?.session_types?.name === 'Social Session' || session?.is_public === true;
};

/**
 * Check if a session is a private session
 * @param {Object} session - The session object with session_types property
 * @returns {boolean} True if the session is a private session
 */
export const isPrivateSession = (session) => {
  return session?.session_types?.name === 'Private Session';
};

/**
 * Check if a session (or type name) is Peg Board.
 * @param {Object|string} sessionOrName - Session object with session_types, type object, or type name
 * @returns {boolean}
 */
export const isPegBoardSession = (sessionOrName) => {
  if (typeof sessionOrName === 'string') {
    return sessionOrName === PEG_BOARD_SESSION_TYPE_NAME;
  }
  return sessionOrName?.session_types?.name === PEG_BOARD_SESSION_TYPE_NAME
    || sessionOrName?.name === PEG_BOARD_SESSION_TYPE_NAME;
};

/**
 * True for the Tournament session_types row. Tournaments are created from
 * `/club/:clubId/tournaments/new`, not session Start / Plan.
 */
export const isTournamentSessionType = (typeOrName) => {
  const name = typeof typeOrName === 'string'
    ? typeOrName
    : typeOrName?.name || typeOrName?.session_types?.name;
  return typeof name === 'string' && name.toLowerCase().includes('tournament');
};

/**
 * Session types hosts may pick when creating or editing sessions.
 * Peg Board is hidden unless REACT_APP_ENABLE_PEG_BOARD=true.
 * Tournament is never offered here.
 */
export const isSelectableSessionType = (type) => {
  if (!type?.name) return false;
  if (isTournamentSessionType(type)) return false;
  if (type.name === PEG_BOARD_SESSION_TYPE_NAME) {
    return isPegBoardEnabled();
  }
  return true;
};

export const getSelectableSessionTypes = (sessionTypes = []) =>
  sessionTypes.filter(isSelectableSessionType);

/**
 * Types allowed in club planning (templates, blocks). Peg Board is included only when flagged on.
 */
export const isClubPlannableSessionType = (type) => {
  if (!type?.name) return false;
  if (type.name === 'Competitive Session' || type.name === 'Social Session') return true;
  return type.name === PEG_BOARD_SESSION_TYPE_NAME && isPegBoardEnabled();
};

/**
 * Check if a session should update ELO ratings
 * @param {Object} session - The session object with session_types property
 * @returns {boolean} True if the session should update ELO ratings
 */
export const shouldUpdateElo = (session) => {
  return getSessionCapabilities(session?.session_types?.name).eloUpdates;
};

/**
 * Check if a session should update partnership stats
 * @param {Object} session - The session object with session_types property
 * @returns {boolean} True if the session should update partnership stats
 */
export const shouldUpdatePartnershipStats = (session) => {
  return getSessionCapabilities(session?.session_types?.name).partnershipStats;
};

/**
 * Check if a session should use auto-matchmaking
 * @param {Object} session - The session object with session_types property
 * @returns {boolean} True if the session should use auto-matchmaking
 */
export const shouldUseAutoMatchmaking = (session) => {
  return getSessionCapabilities(session?.session_types?.name).autoMatchmaking;
};

/**
 * Check if a session should clear courts after game logging
 * @param {Object} session - The session object with session_types property
 * @returns {boolean} True if the session should clear courts after game logging
 */
export const shouldClearCourts = (session) => {
  return getSessionCapabilities(session?.session_types?.name).courtClearing;
};

/**
 * Check if a session should show the player stats tab
 * @param {Object} session - The session object with session_types property
 * @returns {boolean} True if the session should show the player stats tab
 */
export const shouldShowPlayerStatsTab = (session) => {
  return getSessionCapabilities(session?.session_types?.name).playerStatsTab;
};

/**
 * Get session type display name with fallback
 * @param {Object} session - The session object
 * @returns {string} The display name of the session type
 */
export const getSessionTypeDisplayName = (session) => {
  return session?.session_types?.name || 'Competitive Session';
};

const SESSION_TYPE_SHORT_LABEL = {
  [PEG_BOARD_SESSION_TYPE_NAME]: 'Peg Board',
  'Social Session': 'Social',
  'Private Session': 'Private',
  'Competitive Session': 'Competitive',
};

/**
 * Short label for chips (My Club carousel, compact cards).
 * @param {Object|string} sessionOrName
 * @returns {string}
 */
export const getSessionTypeShortLabel = (sessionOrName) => {
  const name = typeof sessionOrName === 'string'
    ? sessionOrName
    : sessionOrName?.session_types?.name || sessionOrName?.name;
  return SESSION_TYPE_SHORT_LABEL[name] || 'Competitive';
};

/**
 * Visual kind for session-type chips and accents.
 * @param {Object|string} sessionOrName
 * @returns {'pegBoard'|'social'|'private'|'competitive'}
 */
export const getSessionTypeKind = (sessionOrName) => {
  if (isPegBoardSession(sessionOrName)) return 'pegBoard';
  const name = typeof sessionOrName === 'string'
    ? sessionOrName
    : sessionOrName?.session_types?.name || sessionOrName?.name;
  if (name === 'Social Session') return 'social';
  if (name === 'Private Session') return 'private';
  return 'competitive';
};
